import { useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { get, ref } from 'firebase/database';
import { auth, database } from '../firebase.js';
import { getInitialAuthToken, getInitialCompanyUid, saveAuthToken, saveCompanyUid } from './authStore.js';

function setAuthCookie(token) {
  if (!token) return;

  if (window.location.hostname.endsWith('jobseeqr.co')) {
    document.cookie = `auth_token=${token}; path=/; domain=.jobseeqr.co; secure`;
  } else {
    document.cookie = `auth_token=${token}; path=/`;
  }
}

function clearAuthCookie() {
  document.cookie = 'auth_token=; path=/; domain=.jobseeqr.co; expires=Thu, 01 Jan 1970 00:00:00 GMT';
  document.cookie = 'auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
}

async function resolveCompanyUid(user) {
  if (!user?.uid) return '';

  try {
    const companySnap = await get(ref(database, `companies/${user.uid}`));
    if (companySnap.exists()) return String(user.uid);
  } catch (_) {
    // Continue to collaborator path
  }

  try {
    const collaboratorSnap = await get(ref(database, `collaborators/${user.uid}`));
    if (collaboratorSnap.exists()) {
      const collab = collaboratorSnap.val() || {};
      if (collab.companyId) return String(collab.companyId);
      if (collab.company_uid) return String(collab.company_uid);
    }
  } catch (_) {
    // Continue to people fallback path
  }

  try {
    const personSnap = await get(ref(database, `people/${user.uid}`));
    if (personSnap.exists()) {
      const person = personSnap.val() || {};
      if (person.company_uid) return String(person.company_uid);
      if (person.companyUid) return String(person.companyUid);
      if (person.company?.uid) return String(person.company.uid);
    }
  } catch (_) {
    // Ignore and fallback to user uid
  }

  return String(user.uid);
}

export function useSigmundAuth() {
  const [user, setUser] = useState(null);
  const [companyUid, setCompanyUidState] = useState(getInitialCompanyUid);
  const [authToken, setAuthTokenState] = useState(getInitialAuthToken);
  const [authReady, setAuthReady] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  function setCompanyUid(nextValue) {
    setCompanyUidState(nextValue);
    saveCompanyUid(nextValue);
  }

  function setAuthToken(nextValue) {
    setAuthTokenState(nextValue);
    saveAuthToken(nextValue);
    if (nextValue) setAuthCookie(nextValue);
  }

  async function loginWithEmail(email, password) {
    setAuthLoading(true);
    setAuthError('');

    try {
      const credential = await signInWithEmailAndPassword(auth, email, password);
      const token = await credential.user.getIdToken(true);
      setAuthToken(token);
    } catch (error) {
      setAuthError(error?.message || 'Login failed.');
      throw error;
    } finally {
      setAuthLoading(false);
    }
  }

  async function logout() {
    setAuthLoading(true);
    setAuthError('');

    try {
      await signOut(auth);
      setUser(null);
      setCompanyUid('');
      setAuthToken('');
      clearAuthCookie();
    } finally {
      setAuthLoading(false);
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (nextUser) => {
      setUser(nextUser);

      if (!nextUser) {
        setCompanyUid('');
        setAuthToken('');
        clearAuthCookie();
        setAuthReady(true);
        return;
      }

      try {
        const token = await nextUser.getIdToken();
        setAuthToken(token);

        const resolvedCompanyUid = await resolveCompanyUid(nextUser);
        setCompanyUid(resolvedCompanyUid);
      } catch (_) {
        // keep fallback values if token/company resolution fails
      } finally {
        setAuthReady(true);
      }
    });

    return () => unsubscribe();
  }, []);

  const isAuthenticated = useMemo(
    () => Boolean(user && companyUid.trim().length > 1 && authToken.trim().length > 0),
    [user, companyUid, authToken]
  );

  return {
    user,
    companyUid,
    setCompanyUid,
    authToken,
    setAuthToken,
    isAuthenticated,
    authReady,
    authLoading,
    authError,
    loginWithEmail,
    logout
  };
}
